import firebase, { COLLECTIONS, collection, db } from 'fb';
import { ReactNode, createContext, useContext, useEffect, useState, useRef } from 'react';
import { openDB } from 'idb';
import type { IDBPDatabase } from 'idb';
import { FavoriteSongsDoc, Song, SongListEntry, SongListEntryMap, SubscriptionType, VideoCategory } from 'types';
import { rememberInLruCache, toListEntry } from 'songCatalogue';
import {
  FullSongAccessIndex,
  PERSISTED_FULL_SONG_LIMIT,
  PersistedFullSong,
  isPersistedSongFresh,
  selectEntriesToEvict,
  timestampToMillis,
  touchAccessIndex,
} from 'fullSongCache';
import { getChordSort } from 'components/Player/utils/multiplePlayerApps';
import { getFirstUrlPath, possibleFirstUrlPaths } from 'components/Player/utils/multiplePlayerApps';
import { initFavorites } from 'components/Player/utils/firebase';
import { useAuth, useIsSchoolAuth } from 'hooks/Auth';
import { useUserWithLoading } from 'hooks/User';
import { userAccessToSong } from 'components/Player/utils/utils';

type SongsContextProps = {
  allSongs: SongListEntry[];
  songsAreLoading: boolean;
  getSearchFilters: () => SearchFilter | undefined;
  setSearchFilters: (
    genreSort: Record<string, unknown>,
    themeSort: Record<string, unknown>,
    videoCategorySort: Record<string, unknown>,
    levelSort: Record<string, unknown>,
    searchTerm: string,
    languageSort: Record<string, unknown>,
    favoriteSort: boolean,
    sortProp: string,
    nameOfSortButton: string,
    chordSort: {
      mustContain: {
        chord: number;
        minor: boolean;
      }[];
      cantContain: {
        chord: number;
        minor: boolean;
      }[];
    },
    shuffleOrder:
      | {
          [key: string]: number;
        }
      | undefined,
    allowExplicitSongs: boolean,
  ) => void;
  filterIsLoading: boolean;
  getAllSongs: () => Promise<SongListEntry[]>;
  getSongById: (id: string) => Promise<Song | string>;
  addToFavorites: (songId: string) => void;
  removeFromFavorites: (songId: string) => void;
  jumpInFilteredSongList: (currentSongId: string, jump: number, userSubscription?: SubscriptionType | undefined) => Promise<string>;
  setFilteredSongs: (songs: SongListEntry[]) => void;
};

type SearchFilter = {
  genreSort: Record<string, unknown>;
  themeSort: Record<string, unknown>;
  videoCategorySort: Record<string, unknown>;
  levelSort: Record<string, unknown>;
  searchTerm: string;
  languageSort: Record<string, unknown>;
  favoriteSort: boolean;
  sortProp: string;
  nameOfSortButton: string;
  chordSort: { mustContain: { chord: number; minor: boolean }[]; cantContain: { chord: number; minor: boolean }[] };
  shuffleOrder: { [key: string]: number } | undefined;
  allowExplicitSongs: boolean;
};

type SearchFilterMap = Map<string, SearchFilter>;

const SongsContext = createContext<SongsContextProps>({
  allSongs: [],
  songsAreLoading: true,
  getSearchFilters: () => undefined,
  setSearchFilters: (
    genreSort: Record<string, unknown>,
    themeSort: Record<string, unknown>,
    videoCategorySort: Record<string, unknown>,
    levelSort: Record<string, unknown>,
    searchTerm: string,
    languageSort: Record<string, unknown>,
    favoriteSort: boolean,
    sortProp: string,
    nameOfSortButton: string,
    chordSort: {
      mustContain: {
        chord: number;
        minor: boolean;
      }[];
      cantContain: {
        chord: number;
        minor: boolean;
      }[];
    },
    shuffleOrder:
      | {
          [key: string]: number;
        }
      | undefined,
    allowExplicitSongs: boolean,
  ) => {},
  filterIsLoading: true,
  getAllSongs: async () => [],
  getSongById: async () => 'does not exist',
  addToFavorites: (songId: string) => {},
  removeFromFavorites: (songId: string) => {},
  jumpInFilteredSongList: async (currentSongId: string, jump: number, userSubscription?: SubscriptionType | undefined) => '',
  setFilteredSongs: (songs: SongListEntry[]) => {},
});

/**
 * Bumped whenever the shape of what we put in IndexedDB changes, so a deploy invalidates every
 * cached catalogue instead of feeding a stale shape to the new code. It is part of the cache keys,
 * which is also how the previous generation's keys become garbage we can delete on sight.
 */
const CACHE_VERSION = 2;
const CACHE_KEYS = {
  songs: `cachedSongs_v${CACHE_VERSION}`,
  deletedIds: `deletedSongsId_v${CACHE_VERSION}`,
  cachedAt: `lastCachedSongDate_v${CACHE_VERSION}`,
  cleanFetchedAt: `lastCleanCachedSongDate_v${CACHE_VERSION}`,
  fullSongIndex: `fullSongAccessIndex_v${CACHE_VERSION}`,
};

/**
 * IndexedDB schema version, separate from CACHE_VERSION: this one is about which object stores
 * exist, not what is in them. Version 2 added `fullSongCache`, where getSongById keeps the full
 * documents it has already paid for.
 */
const CACHE_DB_VERSION = 2;
const SONG_CACHE_STORE = 'songCache';
const FULL_SONG_STORE = 'fullSongCache';
const LEGACY_CACHE_KEYS = ['cachedSongs', 'deletedSongsId', 'lastCachedSongDate', 'lastCleanCachedSongDate', 'forceCleanFetchHasBeenRun'];

/**
 * Songs per query page. Every page is a round trip, so bigger is faster on a school connection,
 * but a page is also the peak amount of *unstripped* song data alive at once — which is what we
 * are protecting low-spec machines from. 100 keeps the whole catalogue inside a handful of round
 * trips while holding well under 10 MB at a time.
 */
const SONG_PAGE_SIZE = 100;

/**
 * How many full song documents to keep. Sized to hold a whole playlist: anything smaller thrashes,
 * because PlaylistInfo walks every song in the playlist and would evict the head of its own list
 * before it got back to it. At roughly 9 KB a document this is well under 2 MB.
 */
const FULL_SONG_CACHE_SIZE = 150;

/**
 * How long getSongById will wait for the in-flight catalogue load before giving up on validating a
 * cached copy and just reading the document. Only ever costs one read, never the song.
 */
const CATALOGUE_WAIT_FOR_VALIDATION_MS = 1500;

/** How long index writes are coalesced for. A playlist opens hundreds of songs in one tick. */
const FULL_SONG_INDEX_SAVE_DELAY_MS = 500;

/**
 * How long to wait for IndexedDB to open before treating the cache as unavailable, and how long to
 * stop trying afterwards. A blocked version upgrade (a tab on the old bundle during a deploy) is
 * otherwise an unbounded wait, and everything the app can do without a cache it must still do.
 */
const CACHE_OPEN_TIMEOUT_MS = 3000;
const CACHE_UNAVAILABLE_FOR_MS = 60000;

const SongsProvider = ({ children }: { children: ReactNode }) => {
  const [auth, authIsLoading] = useAuth();
  const [isSchoolAuth, schoolAuthIsLoading] = useIsSchoolAuth();
  // The catalogue is a large, sustained download. Started the moment auth resolves, it competes with
  // the user document read, and on a slow connection that read is the one that loses - leaving the app
  // signed in with no user data. So hold the catalogue until the user document has settled. `isLoading`
  // here also falls to false when the document is missing or the read failed, so an account without a
  // document still gets its songs rather than waiting forever.
  const { isLoading: userIsLoading } = useUserWithLoading();
  const [allSongs, setAllSongs] = useState<SongListEntry[]>([]);
  const [allSongsMap, setAllSongsMap] = useState<SongListEntryMap>({});
  const filteredSongs = useRef<SongListEntry[]>([]);
  const [deletedSongIds, setDeletedSongIds] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<{ [key: string]: Array<string> }>({});
  const [filterIsLoading, setFilterIsLoading] = useState<boolean>(true);
  const [songsAreLoading, setSongsAreLoading] = useState<boolean>(true);

  /**
   * The catalogue fetch, shared while it is in flight. Without this, a deep link into
   * /player/songs/:id mounts SongPlayer, PlayerCanvas and PlaylistsProvider at once and each of
   * them starts its own full download of the same catalogue.
   */
  const catalogueRequest = useRef<Promise<SongListEntry[]> | null>(null);
  /** Full song documents, fetched on demand by getSongById and kept for a little while. */
  const fullSongCache = useRef<Map<string, Song>>(new Map());
  const fullSongRequests = useRef<Map<string, Promise<Song | string>>>(new Map());
  const favoritesRequest = useRef<Promise<{ [key: string]: Array<string> }> | null>(null);
  /** Which user we have already started a catalogue fetch for. Cleared on failure so a retry can happen. */
  const catalogueRequestedFor = useRef<string | null>(null);
  /** Which user the catalogue currently in `allSongs` belongs to, set only once a load succeeds. */
  const catalogueLoadedFor = useRef<string | null>(null);
  /** Mirrors `favorites` for the async paths, which would otherwise close over the previous user's hearts. */
  const favoritesRef = useRef<{ [key: string]: Array<string> }>({});
  /** Mirrors `deletedSongIds` for the async paths, which would otherwise close over a stale value. */
  const deletedSongIdsRef = useRef<string[]>([]);
  /** Mirrors `allSongsMap`. The persisted full songs are checked against it, from async paths that would otherwise see a render-old map. */
  const allSongsMapRef = useRef<SongListEntryMap>({});
  /** The persisted cache's access order, loaded once and written through, so eviction never reads the songs themselves. */
  const fullSongIndexRef = useRef<FullSongAccessIndex | null>(null);
  const fullSongIndexFlushRef = useRef<{ timer: ReturnType<typeof setTimeout> | null }>({ timer: null });
  /** The index load, shared while in flight, so concurrent writers mutate one index rather than a copy each. */
  const fullSongIndexRequest = useRef<Promise<FullSongAccessIndex> | null>(null);

  const allPlayerApps = possibleFirstUrlPaths as Array<string>;
  const daysBetweenCleanSongFetch = 30;

  useEffect(() => {
    if (authIsLoading) {
      return;
    }
    if (auth && userIsLoading) {
      return;
    }
    if (!auth) {
      // Songs are for signed-in users only (firestore.rules), so there is nothing to wait for.
      setSongsAreLoading(false);
      return;
    }
    if (catalogueRequestedFor.current !== auth.uid) {
      if (catalogueRequestedFor.current !== null) {
        // A different user signed in in this tab. Favourites are per user, so none of the previous
        // user's catalogue, hearts or cached songs may survive into theirs.
        setAllSongs([]);
        setAllSongsMap({});
        allSongsMapRef.current = {};
        setFavorites({});
        setDeletedSongIds([]);
        deletedSongIdsRef.current = [];
        fullSongCache.current.clear();
        fullSongRequests.current.clear();
        catalogueLoadedFor.current = null;
        favoritesRequest.current = null;
        favoritesRef.current = {};
        // The in-flight request belongs to the previous user. Left here, `getAllSongs` would hand
        // the new user A's catalogue — with A's favourites applied — and never fetch theirs.
        catalogueRequest.current = null;
      }
      catalogueRequestedFor.current = auth.uid;
      getAllSongs(auth.uid);
    }
  }, [authIsLoading, auth, userIsLoading]);

  useEffect(() => {
    if (allSongs.length > 0 && filterIsLoading) {
      const { genres, themes, languages, videoCategories } = getFilterValuesForAllSongs(allSongs);
      searchFiltersRef.current = defaultSearchFilter(genres, themes, languages, videoCategories);
      setFilterIsLoading(false);
    }
  }, [allSongs, filterIsLoading]);

  const getDefaultGenreFilter = (allGenres: string[]) => {
    return Object.fromEntries(allGenres.map((genre) => [genre, true]));
  };

  const getDefaultThemeFilter = (allThemes: string[]) => {
    const falseThemes = ['CHRISTIAN', 'MYBAND'];
    return Object.fromEntries(allThemes.map((theme) => [theme, falseThemes.every((falseTheme) => falseTheme !== theme)]));
  };

  const getDefaultLanguageFilter = (allLanguages: string[]) => {
    return Object.fromEntries(allLanguages.map((lang) => [lang, true]));
  };

  const getDefaultVideoCategoryFilter = (playerApp: string, allVideoCategories: string[]) => {
    if (playerApp === 'singplayer' || playerApp === 'choirplayer') {
      // Only LYRICS true
      const defaultValue = Object.fromEntries(allVideoCategories.map((cat) => [cat, false]));
      defaultValue['LYRICS'] = true;
      return defaultValue;
    }
    // Only CHALLENGE false
    const defaultValue = Object.fromEntries(allVideoCategories.map((cat) => [cat, true]));
    defaultValue['CHALLENGE'] = false;
    return defaultValue;
  };

  const defaultSearchFilter = (allGenres: string[], allThemes: string[], allLanguages: string[], allVideoCategories: string[]) => {
    const defaultSearchFilter = new Map();

    allPlayerApps.forEach((playerApp) => {
      defaultSearchFilter.set(playerApp, {
        favoriteSort: false,
        genreSort: getDefaultGenreFilter(allGenres),
        themeSort: getDefaultThemeFilter(allThemes),
        languageSort: getDefaultLanguageFilter(allLanguages),
        videoCategorySort: getDefaultVideoCategoryFilter(playerApp, allVideoCategories),
        levelSort: {},
        searchTerm: '',
        sortProp: '',
        nameOfSortButton: '',
        chordSort: getChordSort(playerApp),
        shuffleOrder: undefined,
        allowExplicitSongs: false,
      });
    });

    return defaultSearchFilter;
  };

  const searchFiltersRef = useRef<SearchFilterMap | undefined>(undefined);

  /**
   * One connection, shared for as long as anything is using it.
   *
   * Every helper below used to open and close its own, which is fine for the handful of catalogue
   * keys but not for the full-song cache: a playlist asks for its songs through one `Promise.all`,
   * and 200 opens of the same database is real time on the machines this is meant to help. The
   * connection is closed again the moment the last operation finishes, so an idle tab never holds
   * one open — an open connection makes `deleteDatabase` return `blocked` and silently do nothing,
   * and would block another tab's version upgrade.
   */
  const cacheDbRef = useRef<{ promise: Promise<IDBPDatabase> | null; users: number; unavailableUntil: number }>({
    promise: null,
    users: 0,
    unavailableUntil: 0,
  });

  /**
   * Runs an operation against the cache, or gives up and returns `fallback`.
   *
   * Nothing here is allowed to be fatal, and the version bump is why. `openDB` at a higher version
   * blocks until every other connection closes, and the build in production opens its v1 connection
   * in `getFromStorage` and never closes it — so during the deploy window one tab left on the old
   * bundle holds v1 open for as long as it lives. An unbounded wait there would hang the catalogue
   * load and `getSongById` behind it, leaving the list on a spinner and songs unopenable until the
   * user found and closed the other tab. Instead the open is raced against a timeout, the cache is
   * marked unavailable for a while, and every caller falls back to Firestore — the same degradation
   * Safari private browsing and blocked site data need, which the code already anticipated.
   */
  async function withCacheDB<T>(operation: (db: IDBPDatabase) => Promise<T> | T, fallback: T): Promise<T> {
    const state = cacheDbRef.current;
    if (state.unavailableUntil > Date.now()) {
      return fallback;
    }
    if (!state.promise) {
      state.promise = openDB('cache', CACHE_DB_VERSION, {
        upgrade(db) {
          // Guarded rather than unconditional: this runs for a first-time visitor (no stores at
          // all) and for someone upgrading from the v1 database (`songCache` already there).
          if (!db.objectStoreNames.contains(SONG_CACHE_STORE)) {
            db.createObjectStore(SONG_CACHE_STORE);
          }
          if (!db.objectStoreNames.contains(FULL_SONG_STORE)) {
            db.createObjectStore(FULL_SONG_STORE);
          }
        },
        blocked() {
          // A tab on the old bundle is holding the old version open. The timeout below is what
          // actually rescues us; this only says why the app went cache-less.
          // eslint-disable-next-line no-console
          console.error('The song cache is blocked by another tab on an older version of the app; falling back to the network');
        },
      });
    }
    const opened = state.promise;
    state.users += 1;
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;
    try {
      const db1 = await Promise.race([
        opened,
        new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), CACHE_OPEN_TIMEOUT_MS);
        }),
      ]);
      if (!db1) {
        // Don't pay the timeout again on every call for the rest of the deploy window.
        state.unavailableUntil = Date.now() + CACHE_UNAVAILABLE_FOR_MS;
        return fallback;
      }
      return await operation(db1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('The song cache is unavailable', error);
      if (state.promise === opened) {
        state.promise = null;
        state.unavailableUntil = Date.now() + CACHE_UNAVAILABLE_FOR_MS;
      }
      return fallback;
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      state.users -= 1;
      if (state.users === 0 && state.promise === opened) {
        state.promise = null;
        // Closing also releases whatever *we* are blocking, and covers the abandoned-open case: if
        // it resolves after the timeout, this is what stops it being held forever.
        opened.then((db1) => db1.close()).catch(() => undefined);
      }
    }
  }

  useEffect(() => {
    // The pre-CACHE_VERSION keys hold a JSON string of the full song documents — tens of megabytes
    // of dead weight in the user's IndexedDB that nothing will ever read again.
    // Best effort: losing the cleanup only costs disk, so never let it break the load.
    // eslint-disable-next-line no-console
    removeFromStorage(LEGACY_CACHE_KEYS).catch((error) => console.error('Failed to clear the legacy song cache', error));
  }, []);

  /**
   * IndexedDB stores structured-cloneable values directly, so the catalogue goes in as an array of
   * objects rather than as a JSON string. The stringify/parse round trip it replaces was megabytes
   * of blocking main-thread work on every cold *and* warm load. Timestamps survive the clone as
   * { seconds, nanoseconds }, which is exactly what JSON.stringify produced before.
   */
  const addToStorage = async (key: string, value: unknown) => withCacheDB((db1) => db1.put(SONG_CACHE_STORE, value, key), undefined);

  const getFromStorage = async (key: string) => withCacheDB((db1) => db1.get(SONG_CACHE_STORE, key), undefined);

  const removeFromStorage = async (keys: string[]) => withCacheDB((db1) => Promise.all(keys.map((key) => db1.delete(SONG_CACHE_STORE, key))), undefined);

  const saveData = (songData: Array<SongListEntry>, deletedSongsId: Array<string>) => {
    addToStorage(CACHE_KEYS.deletedIds, deletedSongsId);
    addToStorage(CACHE_KEYS.songs, songData);
    const now = new Date(firebase.firestore.Timestamp.now().toDate());
    addToStorage(CACHE_KEYS.cachedAt, now.toString());
  };

  const getAllDeletedSongsIdFromFirebase = async () => {
    const [songsInThrashRef, songsDeletedRef] = await Promise.all([
      db.collection(COLLECTIONS.SONGS_TRASH).get(),
      db.collection(COLLECTIONS.SONGS_DELETED).get(),
    ]);
    return songsInThrashRef.docs.map((snapshot) => snapshot.id).concat(songsDeletedRef.docs.map((snapshot) => snapshot.id));
  };

  const getAllSongsFromFirebase = async () => {
    const songsRef = collection<Song>(COLLECTIONS.SONGS).orderBy('lastUpdated', 'desc').orderBy('title');

    return await getAllFirebaseBatches(songsRef);
  };
  const getSongDataFromFirebase = async () => {
    const [songs, newDeletedSongIds] = await Promise.all([getAllSongsFromFirebase(), getAllDeletedSongsIdFromFirebase()]);

    return [songs, newDeletedSongIds] as [SongListEntry[], string[]];
  };

  const getFavoritesFromStorage = async (userId: string) => {
    const cachedFavoritesPlaceholder: string = (await getFromStorage('songIdFavorites_' + userId)) || '';
    let favoritesPlaceholder: { [key: string]: Array<string> } = {};
    if (cachedFavoritesPlaceholder !== '') {
      favoritesPlaceholder = JSON.parse(cachedFavoritesPlaceholder);
    }
    return favoritesPlaceholder;
  };

  const getFavoritesCachedDate = async (userId: string) => {
    const cachedDatePlaceholder: string = (await getFromStorage('lastCachedFavoritesDate_' + userId)) || '';
    if (cachedDatePlaceholder === '') {
      return undefined;
    }
    return new Date(cachedDatePlaceholder);
  };

  const getFavoritesFromFirebase = async (userId: string, date?: Date) => {
    let userFavoriteRef = collection<FavoriteSongsDoc>(COLLECTIONS.FAVORITES).where('userId', '==', userId);
    if (date !== undefined) {
      const timestamp = firebase.firestore.Timestamp.fromDate(date);
      userFavoriteRef = userFavoriteRef.where('lastUpdated', '>', timestamp);
    }
    const favoritesSnapshot = await userFavoriteRef.get();
    if (favoritesSnapshot.size === 0) {
      return {};
    }
    const favoritesDoc = favoritesSnapshot.docs[0].data();
    if (favoritesDoc) {
      return favoritesDoc.favoriteSongs;
    }
    return {};
  };

  const getFavorites = async (userId: string) => {
    // The ref, not the `favorites` state: the user-switch effect calls setFavorites({}) and
    // getAllSongs(newUid) in the same synchronous run, so the closure here still holds the previous
    // user's hearts — this guard would hand them to the new user and skip their read entirely.
    if (Object.keys(favoritesRef.current).length !== 0) {
      return favoritesRef.current;
    }
    // `favorites` is state, so it is still empty for everyone who asked before the first answer
    // came back. Without this, every concurrent caller starts its own read.
    const pending = favoritesRequest.current;
    if (pending) {
      return pending;
    }
    const request = loadFavorites(userId);
    favoritesRequest.current = request;
    try {
      return await request;
    } finally {
      favoritesRequest.current = null;
    }
  };

  const loadFavorites = async (userId: string) => {
    const cachedDate = await getFavoritesCachedDate(userId);
    let newFavorites = await getFavoritesFromFirebase(userId, cachedDate);

    if (Object.keys(newFavorites).length === 0) {
      newFavorites = await getFavoritesFromStorage(userId);
    }

    if (Object.keys(newFavorites).length === 0) {
      newFavorites = (await initFavorites(userId)) as { [player: string]: string[] };
    }

    for (const player of possibleFirstUrlPaths) {
      if (newFavorites[player] === undefined) {
        newFavorites[player] = [];
      }
    }

    saveFavoritesToLocalstorage(userId, newFavorites);
    favoritesRef.current = newFavorites;
    setFavorites(newFavorites);

    return newFavorites;
  };

  const createSongsMapData = (songData: Array<SongListEntry>) => {
    const songMapPlaceholder: SongListEntryMap = {};
    songData.forEach((song) => {
      songMapPlaceholder[song.id] = song;
    });
    return songMapPlaceholder;
  };

  /**
   * The incremental fetch only asks for songs newer than the cached date, so anything it can miss —
   * a bulk migration that rewrote lastUpdated, a write that never landed — would stay missing
   * forever. A periodic clean fetch is the backstop against that drift.
   */
  const checkIfNeedsCleanFetch = async () => {
    const cachedDate: string = (await getFromStorage(CACHE_KEYS.cleanFetchedAt)) || '';
    if (cachedDate.length < 1) {
      return true;
    }
    const now = new Date(firebase.firestore.Timestamp.now().toDate());
    const hoursSinceLastCached = Math.abs(new Date(cachedDate).getTime() - now.getTime()) / 3600000;
    return hoursSinceLastCached >= daysBetweenCleanSongFetch * 24;
  };

  const getDataFromLocalStorage = async () => {
    const empty = { allSongs: [] as SongListEntry[], deletedSongIds: [] as string[], cachedDate: undefined };

    const cachedSongs: SongListEntry[] | undefined = await getFromStorage(CACHE_KEYS.songs);
    const cachedDatePlaceholder: string = (await getFromStorage(CACHE_KEYS.cachedAt)) || '';

    if (!Array.isArray(cachedSongs) || cachedSongs.length < 1 || cachedDatePlaceholder.length < 1) {
      return empty;
    }
    const cachedDeletedIds: string[] = (await getFromStorage(CACHE_KEYS.deletedIds)) || [];

    return { allSongs: cachedSongs, deletedSongIds: cachedDeletedIds, cachedDate: new Date(cachedDatePlaceholder) };
  };

  const getFirebaseBatch = async (
    query: firebase.firestore.Query<Song>,
    lastFetchedDoc: undefined | firebase.firestore.DocumentSnapshot<Song> = undefined,
    batchSize = SONG_PAGE_SIZE,
  ) => {
    let localQuery = query;
    if (lastFetchedDoc !== undefined) {
      localQuery = localQuery.startAfter(lastFetchedDoc);
    }
    localQuery = localQuery.limit(batchSize);

    const documentSnapshot = await localQuery.get();

    // Stripped here rather than at the end so only one page of full song documents is ever alive.
    const songs: SongListEntry[] = documentSnapshot.docs.map((doc) => toListEntry({ ...doc.data(), id: doc.id }));
    return { songs: songs, lastFetchedDoc: documentSnapshot.docs[documentSnapshot.docs.length - 1], done: songs.length < batchSize };
  };

  /**
   * Walks a cursor-paged query to the end. Iterative rather than recursive: the old version
   * rebuilt the whole accumulated array once per page and recursed once per page, both of which
   * scale badly with the size of the catalogue.
   */
  const getAllFirebaseBatches = async (query: firebase.firestore.Query<Song>) => {
    const songs: SongListEntry[] = [];
    let lastFetchedDoc: undefined | firebase.firestore.DocumentSnapshot<Song> = undefined;

    for (;;) {
      const batch = await getFirebaseBatch(query, lastFetchedDoc);
      songs.push(...batch.songs);
      if (batch.done) {
        return songs;
      }
      lastFetchedDoc = batch.lastFetchedDoc;
    }
  };

  /**
   * Drops cached full documents for songs that have changed or gone, in memory and on disk.
   *
   * Fire-and-forget on purpose: this is cache hygiene running inside the catalogue load, and a
   * failure to tidy up must not fail the load. Correctness does not rest on it either — a copy that
   * survives here is still checked against the catalogue's `lastUpdated` before it is served.
   */
  const invalidateCachedSongs = (songIds: string[]) => {
    if (songIds.length === 0) {
      return;
    }
    songIds.forEach((songId) => fullSongCache.current.delete(songId));
    loadFullSongIndex()
      .then((index) => forgetPersistedFullSongs(songIds.filter((songId) => songId in index)))
      // eslint-disable-next-line no-console
      .catch((error) => console.error('Failed to drop cached songs that changed', error));
  };

  const checkForNewChangesInFirebaseAndUpdateData = async (cachedSongs: SongListEntry[], cachedDeletedIds: string[], cachedDate?: Date) => {
    const cachedTimestamp = cachedDate ? firebase.firestore.Timestamp.fromDate(cachedDate) : undefined;

    const sinceCachedDate = (query: firebase.firestore.Query<Song>) => (cachedTimestamp ? query.where('lastUpdated', '>', cachedTimestamp) : query);

    let songsRef = collection<Song>(COLLECTIONS.SONGS).orderBy('lastUpdated', 'desc');
    songsRef = sinceCachedDate(songsRef);
    songsRef = songsRef.orderBy('title');

    // Three independent queries — the old code awaited them one after the other for no reason.
    const [downloadedSongs, thrashSongsDocs, deletedSongsDocs] = await Promise.all([
      getAllFirebaseBatches(songsRef),
      sinceCachedDate(collection<Song>(COLLECTIONS.SONGS_TRASH)).get(),
      sinceCachedDate(collection<Song>(COLLECTIONS.SONGS_DELETED)).get(),
    ]);

    const songsById = new Map(cachedSongs.map((song) => [song.id, song]));
    downloadedSongs.forEach((song) => songsById.set(song.id, song));

    const songsToDelete = thrashSongsDocs.docs.map((snapshot) => snapshot.id).concat(deletedSongsDocs.docs.map((snapshot) => snapshot.id));
    songsToDelete.forEach((deleteId) => songsById.delete(deleteId));

    const allDeletedSongs = songsToDelete.concat(cachedDeletedIds);
    const dataHasChanged = downloadedSongs.length > 0 || songsToDelete.length > 0;

    // Anything the delta touched is a cached full document we now know to be wrong. The freshness
    // check would catch the edits on its own, but dropping them here also clears out songs that are
    // gone entirely, which no catalogue entry will ever speak for again.
    invalidateCachedSongs(downloadedSongs.map((song) => song.id).concat(songsToDelete));

    return [[...songsById.values()], allDeletedSongs, dataHasChanged] as [SongListEntry[], string[], boolean];
  };

  const populateSongsWithFavorites = <T extends { id: string; favorite?: boolean }>(songsArray: Array<T>, userFavorites: Array<string>) => {
    const favoriteIds = new Set(userFavorites);
    return songsArray.map((song) => ({ ...song, favorite: favoriteIds.has(song.id) }));
  };

  /** Loads the catalogue: cached copy plus a delta where we can, everything otherwise. */
  const loadCatalogue = async () => {
    let catalogue: SongListEntry[] = [];
    let newDeleted: string[] = [];
    let cachedDate = undefined;

    const needsCleanFetch = await checkIfNeedsCleanFetch();
    if (!needsCleanFetch) {
      const dataFromLocalStorage = await getDataFromLocalStorage();
      catalogue = dataFromLocalStorage.allSongs;
      newDeleted = dataFromLocalStorage.deletedSongIds;
      cachedDate = dataFromLocalStorage.cachedDate;
    }

    if (needsCleanFetch || catalogue.length === 0) {
      const [songs, deleted] = await getSongDataFromFirebase();
      invalidateCachedSongs(deleted);
      const now = new Date(firebase.firestore.Timestamp.now().toDate());
      await addToStorage(CACHE_KEYS.cleanFetchedAt, now.toString());
      catalogue = songs;
      newDeleted = deleted;
      saveData(songs, deleted);
    } else {
      const [songs, deleted, dataHasChanged] = await checkForNewChangesInFirebaseAndUpdateData(catalogue, newDeleted, cachedDate);
      catalogue = songs;
      newDeleted = deleted;
      if (dataHasChanged) {
        saveData(catalogue, newDeleted);
      }
    }

    setDeletedSongIds(newDeleted);
    deletedSongIdsRef.current = newDeleted;
    return catalogue;
  };

  const getAllSongs = async (userId: string) => {
    if (allSongs.length > 0 && catalogueLoadedFor.current === userId) {
      return allSongs;
    }
    // Everything that needs songs asks for them on mount, and on a deep link into a player that is
    // several components at once. They share one download.
    const pending = catalogueRequest.current;
    if (pending) {
      return pending;
    }

    const request = (async () => {
      try {
        // The favourites read does not depend on the catalogue, so it rides along with it.
        const [catalogue, localFavorites] = await Promise.all([loadCatalogue(), getFavorites(userId)]);
        if (catalogueRequestedFor.current !== userId) {
          // Someone else signed in while this was in flight. Clearing the ref above stops anyone
          // *joining* this request; this stops it publishing A's catalogue into B's session on the
          // way out — the half that a cleared ref cannot prevent.
          return [] as SongListEntry[];
        }
        const withFavorites = populateSongsWithFavorites(catalogue, localFavorites[getFirstUrlPath()] || []);
        // Indexed from `withFavorites`, not from `catalogue`: populateSongsWithFavorites copies each
        // song, and setSongFavoriteFlag writes through this map to the object the list renders.
        allSongsMapRef.current = createSongsMapData(withFavorites);
        setAllSongsMap(allSongsMapRef.current);
        setAllSongs(withFavorites);
        catalogueLoadedFor.current = userId;
        return withFavorites;
      } catch (error) {
        // One offline moment, or an IndexedDB that refuses to open (Safari private browsing,
        // blocked site data), must not leave an empty list claiming the songs don't exist.
        // Releasing the guard lets the next auth change try again, and resolving rather than
        // rejecting keeps every caller's bare `await` intact.
        // eslint-disable-next-line no-console
        console.error('Failed to load the song catalogue', error);
        catalogueRequestedFor.current = null;
        return [] as SongListEntry[];
      }
    })();

    catalogueRequest.current = request;
    setSongsAreLoading(true);
    try {
      return await request;
    } finally {
      // Only if this is still the current request: after a user switch the ref holds the new user's,
      // and clearing it here would strand them exactly as an uncleared one stranded this user.
      // Cleared on failure too: a list that says "no songs" is wrong, but one that spins forever is worse.
      if (catalogueRequest.current === request) {
        catalogueRequest.current = null;
        setSongsAreLoading(false);
      }
    }
  };

  const incrementCounterMap = (counterMap: { [key: string]: number }, incrementValues: string[]) => {
    incrementValues.forEach((value) => {
      if (!(value in counterMap)) {
        counterMap[value] = 0;
      }
      counterMap[value] = counterMap[value] + 1;
    });
    return counterMap;
  };

  const getSortedCounterMapKeys = (counterMap: { [key: string]: number }) => {
    const entries: [string, number][] = Object.entries(counterMap);
    const sortedEntries = entries.sort((a, b) => {
      const countDiff = a[1] - b[1];
      if (countDiff !== 0) {
        return -countDiff;
      }
      return a[0] < b[0] ? -1 : 1;
    });
    const sortedKeys = sortedEntries.map((v) => v[0]);
    return sortedKeys;
  };

  const getFilterValuesForAllSongs = (songs: SongListEntry[]) => {
    let genresCounter: { [key: string]: number } = {};
    let themesCounter: { [key: string]: number } = {};
    let languagesCounter: { [key: string]: number } = {};
    let videoCategoriesCounter: { [key: string]: number } = {};

    songs.forEach((song) => {
      genresCounter = incrementCounterMap(genresCounter, song.genre);
      themesCounter = incrementCounterMap(themesCounter, song.theme);
      languagesCounter = incrementCounterMap(languagesCounter, song.language);
      videoCategoriesCounter = incrementCounterMap(videoCategoriesCounter, [song.videoCategory]);
    });

    themesCounter['NONE'] = 0;

    const genres: string[] = getSortedCounterMapKeys(genresCounter);
    const themes: string[] = getSortedCounterMapKeys(themesCounter);
    const languages: string[] = getSortedCounterMapKeys(languagesCounter);
    const videoCategories: string[] = getSortedCounterMapKeys(videoCategoriesCounter);

    return {
      genres,
      themes,
      languages,
      videoCategories,
    };
  };

  const setSearchFilters = (
    genreSort: Record<string, unknown>,
    themeSort: Record<string, unknown>,
    videoCategorySort: Record<string, unknown>,
    levelSort: Record<string, unknown>,
    searchTerm: string,
    languageSort: Record<string, unknown>,
    favoriteSort: boolean,
    sortProp: string,
    nameOfSortButton: string,
    chordSort: { mustContain: { chord: number; minor: boolean }[]; cantContain: { chord: number; minor: boolean }[] },
    shuffleOrder: { [key: string]: number } | undefined,
    allowExplicitSongs: boolean,
  ) => {
    if (searchFiltersRef.current === undefined) {
      return;
    }
    const player = getFirstUrlPath();
    searchFiltersRef.current.set(player, {
      genreSort,
      themeSort,
      videoCategorySort,
      levelSort,
      searchTerm,
      languageSort,
      favoriteSort,
      sortProp,
      nameOfSortButton,
      chordSort,
      shuffleOrder,
      allowExplicitSongs,
    });
  };

  const getSearchFilters = () => {
    if (searchFiltersRef.current === undefined) {
      return undefined;
    }
    return searchFiltersRef.current.get(getFirstUrlPath());
  };

  /**
   * The access index, loaded once and reconciled against what is actually in the store.
   *
   * Reconciliation matters because the index is written lazily (see `saveFullSongIndexSoon`): a tab
   * closed between a song being written and the index being flushed would otherwise leave a document
   * in IndexedDB that no index entry names, and therefore that eviction can never reach. Reading the
   * keys is cheap — `getAllKeys` never touches the documents themselves.
   */
  const loadFullSongIndex = async (): Promise<FullSongAccessIndex> => {
    if (fullSongIndexRef.current) {
      return fullSongIndexRef.current;
    }
    // Shared while in flight, like `catalogueRequest` and `fullSongRequests`. A playlist runs its
    // songs through one Promise.all, so without this the 30 concurrent `persistFullSong` calls each
    // build their own index before any of them has published one: 29 touches are lost, the LRU
    // decays to id order, and each writer sizes its eviction from a snapshot missing the other 29
    // inserts — so the store sits above PERSISTED_FULL_SONG_LIMIT. Sharing the promise also makes
    // them share the *object*, so each later writer sees the earlier ones' touches.
    const pending = fullSongIndexRequest.current;
    if (pending) {
      return pending;
    }

    const request = (async () => {
      const [stored, storedKeys] = await Promise.all([getFromStorage(CACHE_KEYS.fullSongIndex), withCacheDB((db1) => db1.getAllKeys(FULL_SONG_STORE), [])]);
      const index: FullSongAccessIndex = stored && typeof stored === 'object' ? { ...(stored as FullSongAccessIndex) } : {};
      const now = Date.now();
      const present = new Set(storedKeys.map((key) => String(key)));

      Object.keys(index).forEach((songId) => {
        if (!present.has(songId)) {
          delete index[songId];
        }
      });
      present.forEach((songId) => {
        if (index[songId] === undefined) {
          index[songId] = now;
        }
      });

      fullSongIndexRef.current = index;
      return index;
    })();

    fullSongIndexRequest.current = request;
    try {
      return await request;
    } finally {
      if (fullSongIndexRequest.current === request) {
        fullSongIndexRequest.current = null;
      }
    }
  };

  /**
   * Writes the access index, at most once per `FULL_SONG_INDEX_SAVE_DELAY_MS`.
   *
   * Opening a playlist calls `getSongById` once per song, and writing the index on each of those is
   * a few hundred IndexedDB writes of the same small object. Coalescing costs nothing: the index only
   * orders evictions, so losing the last few hundred milliseconds of it is not a correctness problem,
   * and `loadFullSongIndex` repairs anything the flush missed on the next load.
   */
  const saveFullSongIndexSoon = () => {
    const flush = fullSongIndexFlushRef.current;
    if (flush.timer !== null) {
      return;
    }
    flush.timer = setTimeout(() => {
      flush.timer = null;
      const index = fullSongIndexRef.current;
      if (!index) {
        return;
      }
      // eslint-disable-next-line no-console
      addToStorage(CACHE_KEYS.fullSongIndex, { ...index }).catch((error) => console.error('Failed to save the cached-song index', error));
    }, FULL_SONG_INDEX_SAVE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      const flush = fullSongIndexFlushRef.current;
      if (flush.timer !== null) {
        clearTimeout(flush.timer);
        flush.timer = null;
      }
    };
  }, []);

  const readPersistedFullSong = async (songId: string): Promise<PersistedFullSong | undefined> =>
    withCacheDB((db1) => db1.get(FULL_SONG_STORE, songId), undefined);

  const forgetPersistedFullSongs = async (songIds: string[]) => {
    if (songIds.length === 0) {
      return;
    }
    await withCacheDB(async (db1) => {
      const tx = db1.transaction(FULL_SONG_STORE, 'readwrite');
      songIds.forEach((songId) => tx.store.delete(songId));
      await tx.done;
    }, undefined);
    const index = fullSongIndexRef.current;
    if (index) {
      songIds.forEach((songId) => delete index[songId]);
      saveFullSongIndexSoon();
    }
  };

  /**
   * Writes a full document to IndexedDB so the next visit does not have to read it again.
   *
   * `favorite` is dropped: it is the one field on a song that belongs to the user rather than the
   * song, and it is re-applied from the favourites read on the way out. That also means a user
   * switch does not have to throw this cache away — only the in-memory one, which carries the flag.
   */
  const persistFullSong = async (song: Song) => {
    const now = Date.now();
    const { favorite, ...songWithoutFavorite } = song;
    const record: PersistedFullSong = {
      song: songWithoutFavorite as Song,
      lastUpdatedMillis: timestampToMillis(song.lastUpdated),
      cachedAt: now,
    };

    const index = touchAccessIndex(await loadFullSongIndex(), song.id, now);
    const evicted = selectEntriesToEvict(index, PERSISTED_FULL_SONG_LIMIT);
    evicted.forEach((songId) => delete index[songId]);

    await withCacheDB(async (db1) => {
      const tx = db1.transaction(FULL_SONG_STORE, 'readwrite');
      tx.store.put(record, song.id);
      evicted.forEach((songId) => tx.store.delete(songId));
      await tx.done;
    }, undefined);
    saveFullSongIndexSoon();
  };

  /**
   * The catalogue entry a cached copy is checked against — the only thing that can say whether the
   * copy is still current.
   *
   * On a deep link into /player/songs/:id the catalogue is usually still loading, and it is worth a
   * short wait for it: that load is happening regardless, and if it lands we save a document read.
   * Bounded, because the alternative to waiting is not failure but simply reading the document, and
   * a slow catalogue must never be what keeps a song from opening.
   */
  const catalogueEntryFor = async (songId: string) => {
    const loaded = allSongsMapRef.current[songId];
    if (loaded) {
      return loaded;
    }
    const pending = catalogueRequest.current;
    if (!pending) {
      return undefined;
    }
    await Promise.race([pending, new Promise((resolve) => setTimeout(resolve, CATALOGUE_WAIT_FOR_VALIDATION_MS))]);
    return allSongsMapRef.current[songId];
  };

  /** The cached full document, if there is one and the catalogue still vouches for its `lastUpdated`. */
  const getPersistedFullSongIfFresh = async (songId: string): Promise<Song | undefined> => {
    const persisted = await readPersistedFullSong(songId);
    if (!persisted) {
      return undefined;
    }
    const catalogueEntry = await catalogueEntryFor(songId);
    if (!isPersistedSongFresh(persisted, catalogueEntry, Date.now())) {
      if (catalogueEntry !== undefined) {
        // The catalogue has a newer version of this song, so the copy is not stale-but-usable, it is
        // wrong. Drop it rather than re-checking it on every open. A copy we could not check at all
        // (no catalogue entry yet) is left alone — it may well still be good.
        // eslint-disable-next-line no-console
        forgetPersistedFullSongs([songId]).catch((error) => console.error(`Failed to drop the stale cached song ${songId}`, error));
      }
      return undefined;
    }
    loadFullSongIndex()
      .then((index) => {
        touchAccessIndex(index, songId, Date.now());
        saveFullSongIndexSoon();
      })
      // eslint-disable-next-line no-console
      .catch((error) => console.error('Failed to record use of a cached song', error));
    return persisted.song;
  };

  const rememberFullSong = (song: Song) => rememberInLruCache(fullSongCache.current, song.id, song, FULL_SONG_CACHE_SIZE);

  const fetchFullSong = async (userId: string, songId: string): Promise<Song | string> => {
    // The favourites are needed to render the song's heart and do not depend on the song, so the
    // two reads overlap rather than queueing behind each other.
    const [snapshot, allFavorites] = await Promise.all([collection<Song>(COLLECTIONS.SONGS).doc(songId).get(), getFavorites(userId)]);
    const data = snapshot.data();

    if (snapshot.exists && data) {
      const song: Song = { ...data, id: snapshot.id, favorite: (allFavorites[getFirstUrlPath()] || []).includes(snapshot.id) };
      rememberFullSong(song);
      // Best effort, and deliberately not awaited: the song is ready, and a browser that refuses to
      // write (private browsing, blocked site data) should cost the next visit a read, not this one.
      // eslint-disable-next-line no-console
      persistFullSong(song).catch((error) => console.error(`Failed to cache song ${songId}`, error));
      return song;
    }

    // Telling "deleted" from "never existed" needs the deleted-id list, which the catalogue load
    // produces. Waiting on a load that is already happening is free; paying two document reads per
    // missing song is not, and a playlist of deleted songs opened cold would pay 2N of them.
    if (catalogueRequest.current) {
      await catalogueRequest.current;
    }
    if (deletedSongIdsRef.current.includes(songId)) {
      return 'deleted';
    }
    if (catalogueLoadedFor.current !== null) {
      return 'does not exist';
    }

    const [inThrash, isDeleted] = await Promise.all([
      db.collection(COLLECTIONS.SONGS_TRASH).doc(songId).get(),
      db.collection(COLLECTIONS.SONGS_DELETED).doc(songId).get(),
    ]);
    return inThrash.exists || isDeleted.exists ? 'deleted' : 'does not exist';
  };

  /**
   * Returns the *full* song document, including the playback data the cached catalogue drops.
   *
   * This used to download the entire catalogue whenever the cache was cold, which is why landing
   * on /player/songs/:id was so slow: SongPlayer, PlayerCanvas and PlaylistsProvider each asked
   * for a song and each triggered its own full download to answer. Now it is one document read,
   * shared between concurrent callers and remembered for a while afterwards.
   */
  const getSongById = async (userId: string, songId: string): Promise<Song | string> => {
    const cached = fullSongCache.current.get(songId);
    if (cached) {
      return cached;
    }
    const pending = fullSongRequests.current.get(songId);
    if (pending) {
      return pending;
    }

    // Registered in `fullSongRequests` like a read is, because the IndexedDB lookup can itself wait
    // on the catalogue — long enough for a second caller to arrive and start a duplicate read.
    const request = resolveSong(userId, songId).catch((error) => {
      // Callers fan these out through a bare `Promise.all` (playlists, history, the player), so a
      // single flaky read must degrade to one missing song rather than failing the whole list —
      // 'does not exist' is a value those call sites already filter out.
      // eslint-disable-next-line no-console
      console.error(`Failed to read song ${songId}`, error);
      return 'does not exist' as string;
    });
    fullSongRequests.current.set(songId, request);
    try {
      return await request;
    } finally {
      fullSongRequests.current.delete(songId);
    }
  };

  /**
   * Memory, then IndexedDB, then Firestore.
   *
   * The persisted copy is the point of the disk cache: a song opened yesterday is opened again today
   * for nothing. It is used only while the catalogue still reports the same `lastUpdated`, so an
   * edited song is re-read the first time it is opened after the edit reaches the catalogue.
   */
  const resolveSong = async (userId: string, songId: string): Promise<Song | string> => {
    let persisted: Song | undefined = undefined;
    try {
      persisted = await getPersistedFullSongIfFresh(songId);
    } catch (error) {
      // A cache that cannot be read is not an error the caller should ever see — it just means a read.
      // eslint-disable-next-line no-console
      console.error(`Failed to read cached song ${songId}`, error);
    }

    if (persisted) {
      const allFavorites = await getFavorites(userId);
      const song: Song = { ...persisted, favorite: (allFavorites[getFirstUrlPath()] || []).includes(songId) };
      rememberFullSong(song);
      return song;
    }

    return fetchFullSong(userId, songId);
  };

  const getAllSongsOfUser = async () => {
    if (auth) {
      return getAllSongs(auth.uid);
    }
    return [];
  };

  const getSongByIdWithUser = async (songId: string) => {
    if (auth) {
      return getSongById(auth.uid, songId);
    }
    return 'does not exist';
  };

  const jumpInFilteredSongListLocal = async (
    userId: string,
    currentSongId: string,
    jump: number,
    userSubscription: SubscriptionType | undefined = undefined,
  ) => {
    let songs = filteredSongs.current || [];
    if (songs.length < 1) {
      songs = await getAllSongs(userId);
    }

    const currentIndex = songs.findIndex((song: SongListEntry) => song.id === currentSongId);
    if (currentIndex === -1) {
      return songs[0].id;
    }

    let newIndex = (currentIndex + jump + 2 * songs.length) % songs.length;

    if (userSubscription !== undefined) {
      let song = songs[newIndex];
      let access = userAccessToSong(song, getFirstUrlPath(), userSubscription, isSchoolAuth || schoolAuthIsLoading, false).access;
      const increment = jump > 0 ? 1 : -1;
      while (!access) {
        newIndex = (newIndex + increment + songs.length) % songs.length;
        song = songs[newIndex];
        access = userAccessToSong(song, getFirstUrlPath(), userSubscription, isSchoolAuth || schoolAuthIsLoading, false).access;
      }
    }
    return songs[newIndex].id;
  };

  const jumpInFilteredSongList = async (currentSongId: string, jump: number, userSubscription: SubscriptionType | undefined = undefined) => {
    if (auth) {
      const userId = auth.uid;
      return jumpInFilteredSongListLocal(userId, currentSongId, jump, userSubscription);
    }
    return '';
  };

  /**
   * Mirrors a favourite onto the cached song, so a later re-filter still sees it. The old version
   * binary-searched `allSongs` for the id, but the catalogue is only id-sorted on the incremental
   * path — after a clean fetch it is ordered by lastUpdated, where the search misses and the
   * write lands on `allSongs[-1]`.
   */
  const setSongFavoriteFlag = (songId: string, favorite: boolean) => {
    const song = allSongsMap[songId];
    if (song) {
      song.favorite = favorite;
    }
    const fullSong = fullSongCache.current.get(songId);
    if (fullSong) {
      fullSong.favorite = favorite;
    }
  };

  const saveFavoritesToLocalstorage = async (userId: string, favorites: { [key: string]: Array<string> }) => {
    await addToStorage('songIdFavorites_' + userId, JSON.stringify(favorites));
    const now = new Date(firebase.firestore.Timestamp.now().toDate());
    await addToStorage('lastCachedFavoritesDate_' + userId, now.toString());
  };

  const addToFavoritesInternal = (userId: string, songId: string) => {
    const playerApp = getFirstUrlPath();
    const userFavorites = favoritesRef.current;
    if (userFavorites[playerApp] === undefined) {
      userFavorites[playerApp] = [];
    }
    userFavorites[playerApp].push(songId);

    setSongFavoriteFlag(songId, true);

    const updateObj: { [key: string]: string | firebase.firestore.FieldValue } = {
      lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
      userId: userId,
    };
    updateObj['favoriteSongs.' + playerApp] = firebase.firestore.FieldValue.arrayUnion(songId);

    // add to fb
    db.collection(COLLECTIONS.FAVORITES)
      .doc(userId)
      .update(updateObj)
      .then(() => {
        // save to localstorage
        saveFavoritesToLocalstorage(userId, favoritesRef.current);
      });
  };

  const addToFavorites = (songId: string) => {
    const userId = auth?.uid;
    if (userId) {
      addToFavoritesInternal(userId, songId);
    }
  };

  const removeFromFavoritesInternal = (userId: string, songId: string) => {
    // removes from variable
    const playerApp = getFirstUrlPath();
    const userFavorites = favoritesRef.current;
    const index = (userFavorites[playerApp] || []).indexOf(songId);
    if (index !== -1) {
      userFavorites[playerApp].splice(index, 1);
    }

    setSongFavoriteFlag(songId, false);

    const updateObj: { [key: string]: string | firebase.firestore.FieldValue } = {
      lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
      userId: userId,
    };
    updateObj['favoriteSongs.' + playerApp] = firebase.firestore.FieldValue.arrayRemove(songId);

    // removes from fb
    db.collection(COLLECTIONS.FAVORITES)
      .doc(userId)
      .update(updateObj)
      .then(() => {
        // save to localstorage
        saveFavoritesToLocalstorage(userId, favoritesRef.current);
      });
  };

  const removeFromFavorites = (songId: string) => {
    const userId = auth?.uid;
    if (userId) {
      removeFromFavoritesInternal(userId, songId);
    }
  };

  const setFilteredSongs = (songs: SongListEntry[]) => {
    filteredSongs.current = songs;
  };

  return (
    <SongsContext.Provider
      value={{
        setFilteredSongs: setFilteredSongs,
        allSongs: allSongs,
        songsAreLoading: songsAreLoading,
        getSearchFilters: getSearchFilters,
        setSearchFilters: setSearchFilters,
        filterIsLoading: filterIsLoading,
        getAllSongs: getAllSongsOfUser,
        getSongById: getSongByIdWithUser,
        addToFavorites: addToFavorites,
        removeFromFavorites: removeFromFavorites,
        jumpInFilteredSongList: jumpInFilteredSongList,
      }}>
      {children}
    </SongsContext.Provider>
  );
};

const useSongs = () => {
  const context = useContext(SongsContext);
  return context.allSongs;
};

const useChangeSong = () => {
  const context = useContext(SongsContext);
  return {
    jumpInFilteredSongList: context.jumpInFilteredSongList,
  };
};

const useFilteredSongs = () => {
  const context = useContext(SongsContext);
  return {
    filterIsLoading: context.filterIsLoading,
    songsAreLoading: context.songsAreLoading,
    getSearchFilters: context.getSearchFilters,
    setSearchFilters: context.setSearchFilters,
    setFilteredSongs: context.setFilteredSongs,
  };
};

const useGetSongs = () => {
  const context = useContext(SongsContext);
  return { getAllSongs: context.getAllSongs, getSongById: context.getSongById };
};

const useSongFavorites = () => {
  const context = useContext(SongsContext);
  return { addToFavorites: context.addToFavorites, removeFromFavorites: context.removeFromFavorites };
};
export { SongsProvider, useSongs, useFilteredSongs, useGetSongs, useSongFavorites, useChangeSong };
